import { makeStyles } from 'tss-react/mui';
import AuthLayout from './AuthLayout';
import { useState } from 'react';
import { Alert, Typography, Paper, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'src/redux/hooks';
import {
  isEmpty,
  isValidEmail,
  maxCharacters
} from 'src/helpers/validation-utils';
import { cloneObjectBasic } from 'src/helpers/utils';
import { IUserStore } from 'src/redux/user/types';
import { LoginUser } from 'src/redux/user/userReducer';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SecurityIcon from '@mui/icons-material/Security';
import KeyIcon from '@mui/icons-material/Key';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import GenericButton from 'src/components/GenericButton';
import { DataService } from 'src/services/data-service';
import { useNavigate } from 'react-router-dom';

interface ILoginApiResponse {
  success: boolean;
  token: string;
  message: string;
  data: {
    id: string;
    name: string;
    email: string;
    role: string;
    active: boolean;
  };
}

const useStyles = makeStyles()((theme) => {
  return {
    fullScreenContainer: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      background:
        'linear-gradient(135deg, #2563eb 0%, #1d4ed8 25%, #1e40af 50%, #1e3a8a 75%, #1e293b 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      boxSizing: 'border-box',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute' as const,
        top: '-100px',
        left: '-100px',
        right: '-100px',
        bottom: '-100px',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px),
          linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px, 60px 60px, 20px 20px, 20px 20px',
        animation: 'gridMove 15s linear infinite',
        zIndex: 0
      }
    },
    contentWrapper: {
      width: '100%',
      maxWidth: '1200px',
      display: 'flex',
      gap: '40px',
      alignItems: 'stretch',
      position: 'relative' as const,
      zIndex: 1,
      minHeight: '500px'
    },
    leftPanel: {
      flex: '0 0 45%',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center',
      color: 'white',
      padding: '32px 20px'
    },
    rightPanel: {
      flex: '0 0 55%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    brandContainer: {
      marginBottom: '32px'
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '24px'
    },
    logoIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '16px',
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      border: '2px solid rgba(255, 255, 255, 0.2)'
    },
    brandTitle: {
      fontSize: '28px',
      fontWeight: 800,
      color: 'white',
      margin: 0,
      lineHeight: 1.2,
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
    },
    brandSubtitle: {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.9)',
      fontWeight: 500,
      margin: 0
    },
    featuresList: {
      listStyle: 'none',
      padding: 0,
      margin: '24px 0 0 0'
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px',
      fontSize: '15px',
      color: 'rgba(255, 255, 255, 0.9)',
      fontWeight: 500
    },
    featureIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '10px',
      background: 'rgba(255, 255, 255, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    loginCard: {
      backgroundColor: 'white',
      borderRadius: '20px',
      padding: '32px',
      boxShadow:
        '0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      position: 'relative' as const,
      overflow: 'hidden',
      width: '100%',
      maxWidth: '480px',
      minWidth: '320px',
      maxHeight: '90vh',
      overflowY: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background:
          'linear-gradient(90deg, #2563eb 0%, #1d4ed8 50%, #2563eb 100%)'
      }
    },
    loginHeader: {
      textAlign: 'center' as const,
      marginBottom: '28px'
    },
    loginTitle: {
      fontSize: '24px',
      fontWeight: 700,
      color: '#1f2937',
      margin: '0 0 6px 0'
    },
    loginSubtitle: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: 500,
      margin: 0
    },
    formContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '20px',
      width: '100%'
    },
    inputWrapper: {
      position: 'relative' as const,
      width: '100%'
    },
    customInput: {
      width: '100%',
      '& .MuiInputLabel-root': {
        fontSize: '14px',
        fontWeight: 600,
        color: '#374151',
        transform: 'translate(14px, 14px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)'
        },
        '&.Mui-focused': {
          color: '#2563eb'
        },
        '&.Mui-error': {
          color: '#ef4444'
        }
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fafbfc',
        fontSize: '15px',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        minHeight: '48px',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e1e5e9',
          borderWidth: '1.5px'
        },
        '&:hover': {
          backgroundColor: '#f5f7fa',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563eb'
          }
        },
        '&.Mui-focused': {
          backgroundColor: 'white',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2563eb',
            borderWidth: '2px'
          },
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.08)'
        },
        '&.Mui-error': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ef4444',
            borderWidth: '1.5px'
          },
          backgroundColor: '#fef7f7',
          '&:hover': {
            backgroundColor: '#fef2f2'
          }
        }
      },
      '& .MuiOutlinedInput-input': {
        padding: '12px 14px',
        fontSize: '15px',
        height: 'auto',
        '&::placeholder': {
          color: '#9ca3af',
          opacity: 1
        }
      },
      '& .MuiInputAdornment-root': {
        marginRight: '8px',
        '& .MuiSvgIcon-root': {
          fontSize: '20px',
          color: '#6b7280'
        }
      },
      '& .MuiFormHelperText-root': {
        fontSize: '12px',
        marginTop: '6px',
        marginLeft: '4px',
        fontWeight: 500,
        '&.Mui-error': {
          color: '#ef4444'
        }
      }
    },
    passwordToggle: {
      color: '#6b7280',
      padding: '4px',
      '&:hover': {
        color: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.08)'
      }
    },
    submitButton: {
      marginTop: '16px',
      width: '100%'
    },
    customButton: {
      width: '100%',
      borderRadius: '12px',
      padding: '14px 24px',
      fontSize: '15px',
      fontWeight: 600,
      textTransform: 'none' as const,
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      boxShadow: '0 6px 20px rgba(37, 99, 235, 0.25)',
      transition: 'all 0.2s ease',
      border: 'none',
      color: 'white',
      minHeight: '48px',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
        boxShadow: '0 8px 25px rgba(37, 99, 235, 0.3)',
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0px)'
      },
      '&:disabled': {
        background: '#e5e7eb',
        color: '#9ca3af',
        boxShadow: 'none',
        transform: 'none'
      }
    },
    alertContainer: {
      marginBottom: '20px',
      '& .MuiAlert-root': {
        borderRadius: '10px',
        padding: '12px 16px',
        fontSize: '13px',
        fontWeight: 500,
        border: '1px solid #fecaca',
        backgroundColor: '#fef2f2',
        color: '#991b1b',
        '& .MuiAlert-icon': {
          fontSize: '18px'
        }
      }
    },
    securitySection: {
      marginTop: '24px',
      padding: '16px',
      backgroundColor: '#f0f8ff',
      borderRadius: '10px',
      border: '1px solid #b3d9ff',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    securityIcon: {
      color: '#2563eb',
      fontSize: '20px',
      flexShrink: 0
    },
    securityText: {
      fontSize: '12px',
      color: '#1e40af',
      lineHeight: 1.4,
      fontWeight: 500
    },
    loadingOverlay: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '20px',
      zIndex: 10,
      backdropFilter: 'blur(2px)'
    },
    loadingText: {
      marginTop: '12px',
      color: '#2563eb',
      fontSize: '14px',
      fontWeight: 600
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: '3px solid #e5e7eb',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' }
    },
    '@keyframes gridMove': {
      '0%': {
        transform: 'translate(0, 0)'
      },
      '100%': {
        transform: 'translate(60px, 60px)'
      }
    },
    // Responsive breakpoints
    '@media (max-width: 1024px)': {
      contentWrapper: {
        gap: '24px',
        maxWidth: '900px'
      },
      leftPanel: {
        flex: '0 0 40%',
        padding: '24px 16px'
      },
      rightPanel: {
        flex: '0 0 60%'
      },
      brandTitle: {
        fontSize: '24px'
      },
      brandSubtitle: {
        fontSize: '14px'
      },
      loginCard: {
        padding: '28px',
        maxWidth: '420px'
      }
    },
    '@media (max-width: 768px)': {
      fullScreenContainer: {
        padding: '12px'
      },
      contentWrapper: {
        flexDirection: 'column' as const,
        gap: '16px',
        maxWidth: '400px',
        minHeight: 'auto'
      },
      leftPanel: {
        display: 'none'
      },
      rightPanel: {
        flex: '1'
      },
      loginCard: {
        padding: '24px',
        maxWidth: '100%',
        minWidth: '280px'
      },
      loginTitle: {
        fontSize: '20px'
      },
      loginSubtitle: {
        fontSize: '13px'
      }
    },
    '@media (max-height: 700px)': {
      loginCard: {
        padding: '24px',
        maxHeight: '85vh'
      },
      loginHeader: {
        marginBottom: '20px'
      },
      formContainer: {
        gap: '16px'
      },
      securitySection: {
        marginTop: '16px',
        padding: '12px'
      },
      securityText: {
        fontSize: '11px'
      }
    },
    '@media (max-height: 600px)': {
      fullScreenContainer: {
        padding: '8px'
      },
      loginCard: {
        padding: '20px',
        maxHeight: '90vh'
      },
      loginHeader: {
        marginBottom: '16px'
      },
      loginTitle: {
        fontSize: '20px'
      },
      loginSubtitle: {
        fontSize: '12px'
      },
      formContainer: {
        gap: '14px'
      },
      securitySection: {
        display: 'none' // Hide on very small screens
      }
    }
  };
});

interface ILoginPageFormState {
  values: {
    identifier: string;
    password: string;
  };
  errors: {
    identifier: string;
    password: string;
  };
  touched: {
    identifier: boolean;
    password: boolean;
  };
}

const getInitialLoginFormState = (): ILoginPageFormState => {
  return {
    values: {
      identifier: '',
      password: ''
    },
    errors: {
      identifier: '',
      password: ''
    },
    touched: {
      identifier: false,
      password: false
    }
  };
};

const LoginPage: React.FC = () => {
  const { classes } = useStyles();

  const [pageState, setPageState] = useState<ILoginPageFormState>(
    getInitialLoginFormState()
  );

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onValueChange = (name: string, value: string) => {
    const newPageState = cloneObjectBasic(pageState);
    if (name === 'identifier') {
      newPageState.values.identifier = value;
      newPageState.errors.identifier = '';
    } else if (name === 'password') {
      newPageState.values.password = value;
      newPageState.errors.password = '';
    }
    setErrorMessage('');
    setShowError(false);
    setPageState(newPageState);
  };

  const onBlur = (name: string) => {
    const newPageState = cloneObjectBasic(pageState);
    if (name === 'identifier') {
      newPageState.touched.identifier = true;
      newPageState.errors.identifier = validateField(
        name,
        pageState.values.identifier
      );
    } else if (name === 'password') {
      newPageState.touched.password = true;
      newPageState.errors.password = validateField(
        name,
        pageState.values.password
      );
    }
    setPageState(newPageState);
  };

  const validateField = (name: string, value: string): string => {
    if (name === 'identifier') {
      if (isEmpty(value)) {
        return 'Username or email is required';
      }
    } else if (name === 'password') {
      if (isEmpty(value)) {
        return 'Password is required';
      }
      if (!maxCharacters(value, 50)) {
        return 'Password cannot exceed 50 characters';
      }
    }
    return '';
  };

  const validateForm = () => {
    const newPageState = cloneObjectBasic(pageState);

    newPageState.errors = {
      identifier: validateField('identifier', newPageState.values.identifier),
      password: validateField('password', newPageState.values.password)
    };

    newPageState.touched = {
      identifier: true,
      password: true
    };

    setPageState(newPageState);

    return !(newPageState.errors.identifier || newPageState.errors.password);
  };

  const submit = async () => {
    if (!submitting) {
      setSubmitting(true);
      const isValid = validateForm();
      if (isValid) {
        const data = {
          identifier: pageState.values.identifier,
          password: pageState.values.password
        };

        try {
          console.log('Attempting login to:', 'api/User/login');
          console.log('Login data:', { identifier: data.identifier, password: '***' });

          const loginRequest = await DataService.post('api/User/login', data, {}, false);

          console.log('Login response status:', loginRequest.status);
          console.log('Login response ok:', loginRequest.ok);

          let result: ILoginApiResponse | null = null;
          try {
            result = await loginRequest.json();
            console.log('Login response data:', result);
          } catch (e) {
            console.error('Failed to parse login response as JSON:', e);
          }

          if (loginRequest.ok && result && result.success) {
            const newUser: IUserStore = {
              isAuthenticated: true,
              user: {
                email: result.data.email,
                id: result.data.id,
                name: result.data.name,
                role: result.data.role || '',
                active: result.data.active || false
              },
              token: result.token
            };

            dispatch(LoginUser(newUser));
            setSubmitting(false);
            setErrorMessage('');
            setShowError(false);
            toast.success('Welcome back to RentPro! Login successful.');
            navigate('/');
          } else if (result && result.success === false && result.message) {
            setErrorMessage(result.message);
            setShowError(true);
            toast.error(result.message);
            setSubmitting(false);
          } else if (loginRequest.status === 401) {
            setErrorMessage(
              'Invalid credentials. Please check your username/email and password and try again.'
            );
            setShowError(true);
            toast.error('Invalid credentials');
            setSubmitting(false);
          } else {
            setErrorMessage(
              'Unable to connect to the server. Please try again in a moment.'
            );
            setShowError(true);
            toast.error('Connection error');
            setSubmitting(false);
          }
        } catch (error) {
          console.error('Login network error:', error);
          console.error('Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : 'Unknown',
            stack: error instanceof Error ? error.stack : 'No stack trace'
          });

          setErrorMessage(
            'Network error. Please check your internet connection and try again. ' +
            (error instanceof Error ? `Details: ${error.message}` : '')
          );
          setShowError(true);
          toast.error('Network error');
          setSubmitting(false);
        }
      } else {
        setSubmitting(false);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className={classes.fullScreenContainer}>
      <div className={classes.contentWrapper}>
        {/* Left Panel - Branding */}
        <div className={classes.leftPanel}>
          <div className={classes.brandContainer}>
            <div className={classes.logoSection}>
              <div className={classes.logoIcon}>
                <HomeWorkIcon sx={{ fontSize: '28px' }} />
              </div>
              <div>
                <Typography className={classes.brandTitle}>
                  RentPro Portal
                </Typography>
                <Typography className={classes.brandSubtitle}>
                  Rental Management Platform
                </Typography>
              </div>
            </div>

            <ul className={classes.featuresList}>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <KeyIcon sx={{ fontSize: '18px' }} />
                </div>
                <span>Smart property and asset rental management</span>
              </li>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <CalendarTodayIcon sx={{ fontSize: '18px' }} />
                </div>
                <span>Automated booking and scheduling system</span>
              </li>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <PaymentIcon sx={{ fontSize: '18px' }} />
                </div>
                <span>Integrated payment processing and invoicing</span>
              </li>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <PeopleIcon sx={{ fontSize: '18px' }} />
                </div>
                <span>Tenant and property owner management</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className={classes.rightPanel}>
          <Paper className={classes.loginCard} elevation={0}>
            {submitting && (
              <div className={classes.loadingOverlay}>
                <div className={classes.spinner}></div>
                <Typography className={classes.loadingText}>
                  Authenticating your credentials...
                </Typography>
              </div>
            )}

            <div className={classes.loginHeader}>
              <Typography className={classes.loginTitle}>
                Welcome to RentPro
              </Typography>
              <Typography className={classes.loginSubtitle}>
                Please sign in to access your rental management dashboard
              </Typography>
            </div>

            {showError && errorMessage && (
              <div className={classes.alertContainer}>
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{
                    borderRadius: '10px',
                    '& .MuiAlert-action': {
                      padding: '2px 4px'
                    }
                  }}
                >
                  {errorMessage}
                </Alert>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              onKeyDown={handleKeyPress}
              className={classes.formContainer}
            >
              <div className={classes.inputWrapper}>
                <TextField
                  label="Username or Email"
                  type="text"
                  value={pageState.values.identifier}
                  onChange={(e) => onValueChange('identifier', e.target.value)}
                  name="identifier"
                  error={
                    !!(
                      pageState.touched.identifier &&
                      pageState.errors.identifier
                    )
                  }
                  helperText={
                    pageState.touched.identifier
                      ? pageState.errors.identifier
                      : ''
                  }
                  onBlur={() => onBlur('identifier')}
                  disabled={submitting}
                  placeholder="Enter your username or email"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    )
                  }}
                  variant="outlined"
                  className={classes.customInput}
                />
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={pageState.values.password}
                  onChange={(e) => onValueChange('password', e.target.value)}
                  name="password"
                  error={
                    !!(pageState.touched.password && pageState.errors.password)
                  }
                  helperText={
                    pageState.touched.password ? pageState.errors.password : ''
                  }
                  onBlur={() => onBlur('password')}
                  disabled={submitting}
                  placeholder="Enter your password"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={submitting}
                          className={classes.passwordToggle}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  variant="outlined"
                  className={classes.customInput}
                />
              </div>

              <div className={classes.submitButton}>
                <GenericButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  onClick={submit}
                  disabled={submitting}
                  text={submitting ? 'Signing In...' : 'Access RentPro'}
                  className={classes.customButton}
                />
              </div>
            </form>

            <div className={classes.securitySection}>
              <SecurityIcon className={classes.securityIcon} />
              <Typography className={classes.securityText}>
                Your rental data is protected with bank-level security. All
                transactions and tenant information are encrypted and securely
                stored.
              </Typography>
            </div>
          </Paper>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
